(() => {
  'use strict';
  if (customElements.get('salabom-cycle-tracker')) return;

  const DAY = 86400000;
  const parseDate = (value) => new Date(`${value}T12:00:00`);
  const isoDate = (date) => {
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 10);
  };
  const addDays = (value, days) => isoDate(new Date(parseDate(value).getTime() + days * DAY));
  const daysBetween = (from, to) => Math.round((parseDate(to) - parseDate(from)) / DAY);
  const median = (numbers) => {
    const sorted = [...numbers].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[middle] : Math.round((sorted[middle - 1] + sorted[middle]) / 2);
  };
  const formatDate = (value, options = { day: 'numeric', month: 'short' }) => new Intl.DateTimeFormat('sk-SK', options).format(parseDate(value));

  class SalabomCycleTracker extends HTMLElement {
    connectedCallback() {
      this.storageKey = this.dataset.storageKey;
      this.form = this.querySelector('[data-entry-form]');
      this.dateInput = this.form.elements.date;
      this.entries = this.readEntries();
      this.dateInput.value = isoDate(new Date());
      this.bind();
      this.renderAll();
      this.loadDate(this.dateInput.value);
    }

    bind() {
      this.querySelectorAll('[data-tab]').forEach((button) => button.addEventListener('click', () => this.openTab(button.dataset.tab)));
      this.form.addEventListener('submit', (event) => { event.preventDefault(); this.save(); });
      this.dateInput.addEventListener('change', () => this.loadDate(this.dateInput.value));
      this.querySelector('[data-download-csv]').addEventListener('click', () => this.downloadCsv());
      this.querySelector('[data-clear-all]').addEventListener('click', () => this.clearAll());
      this.querySelectorAll('[data-print-template]').forEach((button) => button.addEventListener('click', () => this.printTemplate(button.dataset.printTemplate)));
      this.querySelector('[data-history]').addEventListener('click', (event) => {
        const button = event.target.closest('[data-delete-date]');
        if (button) this.deleteEntry(button.dataset.deleteDate);
      });
      window.addEventListener('afterprint', () => this.querySelectorAll('[data-print-sheet]').forEach((sheet) => sheet.classList.remove('is-printing')));
    }

    readEntries() {
      try {
        const value = JSON.parse(localStorage.getItem(this.storageKey));
        return Array.isArray(value) ? value.filter((item) => item && /^\d{4}-\d{2}-\d{2}$/.test(item.date)) : [];
      } catch (_error) { return []; }
    }

    persist() {
      try { localStorage.setItem(this.storageKey, JSON.stringify(this.entries)); }
      catch (_error) { this.setStatus('Údaje sa v tomto prehliadači nepodarilo uložiť.', true); }
    }

    openTab(tab) {
      this.querySelectorAll('[data-tab]').forEach((button) => button.setAttribute('aria-selected', String(button.dataset.tab === tab)));
      this.querySelectorAll('[data-panel]').forEach((panel) => { panel.hidden = panel.dataset.panel !== tab; });
      if (tab === 'insights') this.renderAll();
      this.track('salabom_cycle_tracker_tab', { tracker_tab: tab });
    }

    formValue() {
      const data = new FormData(this.form);
      return {
        date: String(data.get('date')),
        flow: Number(data.get('flow')),
        periodStart: data.get('periodStart') === 'on',
        mood: Number(data.get('mood')),
        energy: Number(data.get('energy')),
        pain: Number(data.get('pain')),
        symptoms: data.getAll('symptoms').map(String),
        notes: String(data.get('notes') || '').trim().slice(0, 300),
        updatedAt: new Date().toISOString()
      };
    }

    save() {
      if (!this.form.reportValidity()) return;
      const entry = this.formValue();
      const index = this.entries.findIndex((item) => item.date === entry.date);
      if (index >= 0) this.entries[index] = entry; else this.entries.push(entry);
      this.entries.sort((a, b) => a.date.localeCompare(b.date));
      this.persist();
      this.setStatus(index >= 0 ? 'Zápis bol aktualizovaný.' : 'Dnešný zápis je uložený.');
      this.showRecommendation(entry);
      this.renderAll();
      this.track('salabom_cycle_entry_saved', { entry_count: this.entries.length, is_update: index >= 0 });
    }

    setStatus(message, error = false) {
      const status = this.querySelector('[data-form-status]');
      status.textContent = message;
      status.style.color = error ? '#a62b4d' : '';
    }

    loadDate(date) {
      const entry = this.entries.find((item) => item.date === date);
      this.form.reset();
      this.dateInput.value = date;
      if (!entry) { this.querySelector('[data-recommendation]').hidden = true; this.setStatus(''); return; }
      this.form.elements.flow.value = String(entry.flow);
      this.form.elements.periodStart.checked = Boolean(entry.periodStart);
      ['mood', 'energy', 'pain'].forEach((name) => {
        const input = this.form.querySelector(`[name="${name}"][value="${entry[name]}"]`);
        if (input) input.checked = true;
      });
      this.form.querySelectorAll('[name="symptoms"]').forEach((input) => { input.checked = entry.symptoms.includes(input.value); });
      this.form.elements.notes.value = entry.notes;
      this.setStatus('Tento deň už máš uložený. Uložením ho aktualizuješ.');
      this.showRecommendation(entry);
    }

    showRecommendation(entry) {
      let title = 'Dopraj si udržateľné tempo';
      let copy = 'Vyber si jednu prioritu a jednu krátku pauzu bez obrazovky. Večer si všimni, či sa energia alebo nálada zmenili.';
      if (entry.pain >= 8) {
        title = 'Silnú bolesť nemusíš iba vydržať';
        copy = 'Ak ťa bolesť vyradí z fungovania, je nová alebo sa zhoršuje, kontaktuj zdravotníka. Pri náhlej silnej bolesti, mdlobe alebo možnom tehotenstve vyhľadaj urgentnú pomoc.';
      } else if (entry.flow >= 4 && entry.energy <= 2) {
        title = 'Dnes zníž záťaž a sleduj krvácanie';
        copy = 'Odlož jednu nepovinnú úlohu, dopraj si jedlo a tekutiny. Ak je krvácanie nezvyčajne silné alebo sa spája so slabosťou, závratmi či dýchavičnosťou, kontaktuj zdravotníka.';
      } else if (entry.energy <= 2) {
        title = 'Šetri kapacitu, nie sebadôveru';
        copy = 'Zúž dnešok na jednu nutnú vec. Požiadaj o jednu konkrétnu pomoc a vlož si 15 minút oddychu ešte pred úplným vyčerpaním.';
      } else if (entry.mood <= 2 || entry.symptoms.some((item) => ['Úzkosť', 'Smutok', 'Podráždenosť'].includes(item))) {
        title = 'Menej podnetov, jasnejšia prosba';
        copy = 'Daj si 10 minút bez notifikácií a pomenuj jednu potrebu: počúvanie, praktická pomoc alebo priestor. Skús ju povedať priamo človeku, ktorému dôveruješ.';
      } else if (entry.energy >= 4 && entry.mood >= 4) {
        title = 'Využi energiu, ale nechaj si rezervu';
        copy = 'Je to dobrý deň na dôležitú vec alebo príjemný kontakt. Nenechaj však vyššiu energiu automaticky zaplniť novými záväzkami.';
      }
      const card = this.querySelector('[data-recommendation]');
      card.querySelector('[data-rec-title]').textContent = title;
      card.querySelector('[data-rec-copy]').textContent = copy;
      card.hidden = false;
    }

    cycleData() {
      const starts = this.entries.filter((item) => item.periodStart).map((item) => item.date).sort();
      const lengths = starts.slice(1).map((start, index) => daysBetween(starts[index], start)).filter((length) => length >= 15 && length <= 60);
      const average = lengths.length ? median(lengths) : null;
      const lastStart = starts.at(-1) || null;
      return { starts, lengths, average, lastStart, next: average && lastStart ? addDays(lastStart, average) : null };
    }

    renderAll() {
      const cycle = this.cycleData();
      this.querySelector('[data-stat-entries]').textContent = String(this.entries.length);
      this.querySelector('[data-stat-cycle]').textContent = cycle.average ? `${cycle.average} dní` : '—';
      this.querySelector('[data-stat-cycle-note]').textContent = cycle.lengths.length ? `z ${cycle.lengths.length} cyklov` : 'treba 2 začiatky';
      this.querySelector('[data-stat-next]').textContent = cycle.next ? formatDate(cycle.next) : '—';
      this.renderChart();
      this.renderForecast(cycle);
      this.renderHistory();
    }

    renderChart() {
      const wrap = this.querySelector('[data-chart]');
      const entries = this.entries.slice(-30);
      if (!entries.length) { wrap.innerHTML = '<div class="cycle-tracker__empty">Po prvom zápise sa tu zobrazí graf.</div>'; return; }
      const width = 900, height = 230, left = 32, right = 12, top = 15, bottom = 30;
      const plotWidth = width - left - right, plotHeight = height - top - bottom;
      const x = (index) => left + (entries.length === 1 ? plotWidth / 2 : index * plotWidth / (entries.length - 1));
      const y = (value) => top + (5 - value) * plotHeight / 4;
      const points = (key) => entries.map((entry, index) => `${x(index)},${y(key === 'pain' ? entry.pain / 2 : entry[key])}`).join(' ');
      const grids = [1, 2, 3, 4, 5].map((value) => `<line class="grid" x1="${left}" y1="${y(value)}" x2="${width - right}" y2="${y(value)}"/><text x="8" y="${y(value) + 4}">${value}</text>`).join('');
      const labels = entries.map((entry, index) => (index === 0 || index === entries.length - 1 || index % Math.ceil(entries.length / 6) === 0) ? `<text x="${x(index)}" y="${height - 7}" text-anchor="middle">${formatDate(entry.date)}</text>` : '').join('');
      wrap.innerHTML = `<svg viewBox="0 0 ${width} ${height}" role="img" aria-label="Graf nálady, energie a bolesti za posledných 30 zápisov">${grids}<polyline class="mood" points="${points('mood')}"/><polyline class="energy" points="${points('energy')}"/><polyline class="pain" points="${points('pain')}"/>${labels}</svg>`;
    }

    comparableEntries(targetDay, cycle) {
      return this.entries.map((entry) => {
        const previousStart = cycle.starts.filter((start) => start <= entry.date).at(-1);
        return previousStart ? { ...entry, cycleDay: daysBetween(previousStart, entry.date) + 1 } : null;
      }).filter((entry) => entry && Math.abs(entry.cycleDay - targetDay) <= 2);
    }

    renderForecast(cycle) {
      const card = this.querySelector('[data-forecast]');
      const title = card.querySelector('h3');
      const copy = card.querySelector('p:last-child');
      if (this.entries.length < 10 || cycle.starts.length < 2 || !cycle.average || !cycle.lastStart) {
        title.textContent = 'Zatiaľ zbierame tvoj vzorec';
        copy.textContent = `Na prvú opatrnú predpoveď potrebujeme aspoň 10 zápisov a 2 označené začiatky menštruácie. Aktuálny počet: ${this.entries.length} zápisov a ${cycle.starts.length} označených začiatkov.`;
        return;
      }
      const currentDay = Math.max(1, daysBetween(cycle.lastStart, isoDate(new Date())) + 1);
      const comparable = this.comparableEntries(currentDay, cycle);
      if (comparable.length < 3) {
        title.textContent = `Ďalší začiatok môže byť približne ${formatDate(cycle.next)}`;
        copy.textContent = `Odhad vychádza z mediánu ${cycle.lengths.length} dokončených cyklov (${cycle.average} dní). Na predpoveď pocitov v tejto časti cyklu ešte chýbajú porovnateľné zápisy.`;
        return;
      }
      const avg = (key) => comparable.reduce((sum, item) => sum + item[key], 0) / comparable.length;
      const mood = avg('mood'), energy = avg('energy'), pain = avg('pain');
      const feeling = pain >= 6 ? 'výraznejšia bolesť' : energy <= 2.5 ? 'nižšia energia' : mood <= 2.5 ? 'citlivejšia nálada' : energy >= 3.8 ? 'viac energie' : 'skôr vyrovnaný deň';
      title.textContent = `Okolo ${currentDay}. dňa sa u teba objavuje ${feeling}`;
      copy.textContent = `Vychádza to z ${comparable.length} tvojich zápisov v podobných dňoch cyklu. Ber to ako možnosť, nie istotu; dnešok môžu ovplyvniť spánok, stres, choroba aj ďalšie okolnosti.`;
    }

    renderHistory() {
      const wrap = this.querySelector('[data-history]');
      const items = [...this.entries].reverse().slice(0, 10);
      if (!items.length) { wrap.innerHTML = '<div class="cycle-tracker__empty">Zatiaľ tu nie sú žiadne zápisy.</div>'; return; }
      wrap.replaceChildren(...items.map((entry) => {
        const article = document.createElement('article');
        const date = document.createElement('strong');
        const summary = document.createElement('p');
        const button = document.createElement('button');
        date.textContent = formatDate(entry.date, { day: 'numeric', month: 'long', year: 'numeric' });
        summary.textContent = `Nálada ${entry.mood}/5 · Energia ${entry.energy}/5 · Bolesť ${entry.pain}/10${entry.symptoms.length ? ` · ${entry.symptoms.slice(0, 3).join(', ')}` : ''}`;
        button.type = 'button'; button.dataset.deleteDate = entry.date; button.textContent = 'Vymazať'; button.setAttribute('aria-label', `Vymazať zápis z ${date.textContent}`);
        article.append(date, summary, button); return article;
      }));
    }

    deleteEntry(date) {
      if (!window.confirm(`Naozaj vymazať zápis z ${formatDate(date, { dateStyle: 'long' })}?`)) return;
      this.entries = this.entries.filter((entry) => entry.date !== date);
      this.persist(); this.renderAll();
      if (this.dateInput.value === date) this.loadDate(date);
    }

    clearAll() {
      if (!this.entries.length || !window.confirm('Naozaj natrvalo vymazať všetky lokálne zápisy z tohto zariadenia?')) return;
      this.entries = []; this.persist(); this.renderAll(); this.form.reset(); this.dateInput.value = isoDate(new Date()); this.querySelector('[data-recommendation]').hidden = true;
      this.track('salabom_cycle_data_cleared');
    }

    downloadCsv() {
      if (!this.entries.length) { this.openTab('checkin'); this.setStatus('Najprv si ulož aspoň jeden zápis.', true); return; }
      const escape = (value) => `"${String(value ?? '').replaceAll('"', '""')}"`;
      const rows = [['datum', 'prvy_den_menstruacie', 'krvacanie_0_4', 'nalada_1_5', 'energia_1_5', 'bolest_0_10', 'priznaky', 'poznamka'], ...this.entries.map((item) => [item.date, item.periodStart ? 'ano' : 'nie', item.flow, item.mood, item.energy, item.pain, item.symptoms.join('; '), item.notes])];
      const blob = new Blob([`\uFEFF${rows.map((row) => row.map(escape).join(',')).join('\n')}`], { type: 'text/csv;charset=utf-8' });
      const link = document.createElement('a'); link.href = URL.createObjectURL(blob); link.download = `salabom-moj-rytmus-${isoDate(new Date())}.csv`; link.click(); URL.revokeObjectURL(link.href);
      this.track('salabom_cycle_data_downloaded', { entry_count: this.entries.length });
    }

    printTemplate(name) {
      const sheet = this.querySelector(`[data-print-sheet="${name}"]`);
      if (!sheet) return;
      sheet.classList.add('is-printing'); window.print();
      this.track('salabom_cycle_template_printed', { template_name: name });
    }

    track(event, detail = {}) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event, feature_id: 'cycle_tracker', page_path: location.pathname, ...detail });
    }
  }

  customElements.define('salabom-cycle-tracker', SalabomCycleTracker);
})();
